import React from 'react'
import { useTranslation } from 'react-i18next'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import { format } from 'date-fns'

import { makeStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import IconButton from '@material-ui/core/IconButton'
import Button from '@material-ui/core/Button'
import InputBase from '@material-ui/core/InputBase'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CloseIcon from '@material-ui/icons/Close'
import Slide from '@material-ui/core/Slide'
import Paper from '@material-ui/core/Paper'
import MapIcon from '@material-ui/icons/Map'
import EditIcon from '@material-ui/icons/Edit'
import GetAppIcon from '@material-ui/icons/GetApp'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'

import StatsGraph from '@graniot/common/components/map/dialog/graphs/StatsGraph'
import Comparator from '@graniot/common/components/map/dialog/comparator/Comparator'
import Crops from '@graniot/common/components/map/dialog/crops/Crops'
import AdvancedData from '@graniot/common/components/map/dialog/advanced/AdvancedData'
import Fertilizer from '@graniot/common/components/map/dialog/fertilizer/Fertilizer'
import EditParcel from '@graniot/common/components/map/dialog/edit/EditParcel'
import DownloadTiffs from '@graniot/common/components/map/dialog/download_tiffs/DownloadTiffs'
import PlaguesList from '@graniot/common/components/map/dialog/palgues/PlaguesList'
import TreeMap from '@graniot/common/components/map/dialog/tree_counting/TreeMap'

import { Forecast } from '@graniot/common/components/map/dialog/forecast/general/Forecast'
import NotesList from '@graniot/common/components/map/dialog/notes/NotesList'
import { useAuth } from '@graniot/common/context/AuthContext'
import TabPanel from '@graniot/common/components/map/dialog/TabPanel'
import { useMutateProfileIntro } from '@graniot/common/adapters/profile'
import DialogIntro from './DialogIntro'
import VegetationNutrition from '@graniot/common/components/map/dialog/vegetation_nutrition/VegetationNutrition'
import { useParcelStatistics } from '@graniot/common/adapters/sentinel'
import { getLayerWithPriority } from '@graniot/common/utils'
import EnabledTabs from './EnabledTabs'

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: 'relative',
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  imgParcel: {
    height: 150,
  },
  root: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    maxWidth: 300,
    opacity: 0.8,
  },
}))

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const availableTabs = [
  { key: "COMPARISON_TAB_VIEW", index: 0 },
  { key: "GRAPHS_TAB_VIEW", index: 3 },
  { key: "NOTES_TAB_VIEW", index: 2 },
  { key: "ZONING_TAB_VIEW", index: 6 },
  { key: "WEATHER_FORECAST_TAB_VIEW", index: 1 },
  { key: "ADVANCED_TAB_VIEW", index: 4 },
  { key: "TREE_ANALYSIS_TAB_VIEW", index: 8 },
  { key: "CROPS_TAB_VIEW", index: 5 },
];

const image_placeholder = 'https://via.placeholder.com/400'

function FullScreenInfoDialog({
  parcels,
  open,
  setPayload,
  parcel,
  layers,
  selectedResolution,
  width,
  dispatchFarms,
  dispatchAddedParcels,
  farms,
  farmsHectares,
  openEditParcel,
  setOpenEditParcel,
  selectedFarm,
}) {
  const classes = useStyles()
  const { t } = useTranslation()
  const { loggedUser, isClientInFreeTrial, setUserProfile } = useAuth()

  const [selectedParcel, setSelectedParcel] = React.useState(parcels.find((p) => p.id === parcel.id))
  const initialTabIndex = availableTabs.find(tab =>
    !loggedUser?.profile?.embedded_conf?.hasOwnProperty(tab.key) ||
    loggedUser?.profile?.embedded_conf?.[tab.key]
  )?.index || 0;
  
  const [value, setValue] = React.useState(initialTabIndex);
  const [editParcel, setEditParcel] = React.useState(false)
  const [downloadTiffs, setDownloadTiffs] = React.useState(false)
  const [isUpdating, setIsUpdating] = React.useState(false)

  const [selectedLayer, setSelectedLayer] = React.useState(getLayerWithPriority(layers))
  const [maxCC, setMaxCC] = React.useState(0.2)
  const [from_date, setFromDate] = React.useState(
    `01/01/${selectedResolution.id === 1
      ? 2017
      : selectedResolution.id === 2
        ? 2019
        : 2021
    }`
  )
  const [toDate, setToDate] = React.useState(format(new Date(), 'yyyy-LL-dd'))

  // images for the comparator section
  const [baseImage, setBaseImage] = React.useState({
    image: image_placeholder,
  })
  const [comparedImage, setComparedImage] = React.useState({
    image: image_placeholder,
  })

  const mutateProfile = useMutateProfileIntro()

  const showBadge = (tab) => {
    if (loggedUser.profile.user_seen_functionality)
      return loggedUser.profile.user_seen_functionality[tab]
    return true
  }

  const handleChange = (event, newValue) => {
    setValue(newValue)
    if (!loggedUser.is_superuser) {
      let user_seen_functionality = {
        ...loggedUser.profile.user_seen_functionality,
      }

      switch (newValue) {
        case 0:
          if (user_seen_functionality['comparation']) return
          user_seen_functionality['comparation'] = true
          break
        case 1:
          if (user_seen_functionality['wheather_forecast']) return
          user_seen_functionality['wheather_forecast'] = true
          break
        case 2:
          if (user_seen_functionality['notes']) return
          user_seen_functionality['notes'] = true
          break
        case 3:
          if (user_seen_functionality['graphics']) return
          user_seen_functionality['graphics'] = true
          break
        case 4:
          if (user_seen_functionality['advanced']) return
          user_seen_functionality['advanced'] = true
          break
        case 5:
          if (user_seen_functionality['crops']) return
          user_seen_functionality['crops'] = true
          break
        case 6:
          if (user_seen_functionality['fertilizers']) return
          user_seen_functionality['fertilizers'] = true
          break
        case 7:
          if (user_seen_functionality['plagues']) return
          user_seen_functionality['plagues'] = true
          break
        default:
          return
      }

      mutateProfile.mutate({
        ...loggedUser.profile,
        user_seen_functionality,
      })
      setUserProfile({ ...loggedUser.profile, user_seen_functionality })
    }
  }

  const handleClose = () => {
    setPayload({ open: false })
  }

  const handleEditName = () => {
    setOpenEditParcel(!openEditParcel)
  }

  const handleDownloadTiffs = () => {
    setDownloadTiffs(!downloadTiffs)
  }

  const handleArrowBackClick = () => {
    // Allow back navigation if not actively updating
    if (!isUpdating) {
      setOpenEditParcel(false)
      setDownloadTiffs(false)
      if (loggedUser.profile?.embedded_conf?.only_edit_parcel)
        setPayload({ open: false })
    }
    // If updating, do nothing - ignore back button
  }
  const handleSelectedLayer = (event, newValue) => {
    setSelectedLayer(newValue)
  }

  const {
    isLoading: isLoadingParcelFISHistory,
    data: dataParcelFISHistory,
    error: errorParcelFISHistory,
    refetch: refetchParcelFISHistory,
  } = useParcelStatistics(
    parcel.id,
    selectedLayer.key,
    maxCC,
    from_date,
    toDate
  )

  React.useEffect(() => {
    if (selectedLayer) {
      refetchParcelFISHistory()
    }
  }, [selectedLayer])



  return (
    <>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBar className={classes.appBar}>
          <Toolbar>
            {openEditParcel || downloadTiffs ? (
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleArrowBackClick}
              >
                <ArrowBackIcon />
              </IconButton>
            ) : (
              <>
                <IconButton
                  id="close-btn-full-dialog"
                  edge="start"
                  color="inherit"
                  onClick={handleClose}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
                <Autocomplete
                  id="parcels-list-full-dialog"
                  value={selectedParcel}
                  onChange={(event, newValue) => {
                    setSelectedParcel(newValue)
                  }}
                  style={{ maxWidth: 300 }}
                  getOptionLabel={(option) =>
                    option.properties.name
                  }
                  options={parcels}
                  renderInput={(params) => (
                    <Paper
                      className={classes.root}
                      ref={params.InputProps.ref}
                    >
                      <IconButton>
                        <MapIcon />
                      </IconButton>
                      <InputBase
                        style={{
                          backgroundColor:
                            'transparent',
                        }}
                        type="text"
                        {...params.inputProps}
                        InputProps={{
                          className: classes.input,
                        }}
                      />
                    </Paper>
                  )}
                />
              </>
            )}

            <div style={{ flexGrow: 1 }} />
            {downloadTiffs || openEditParcel
              ? null
              : loggedUser.is_superuser && (
                <Button
                  id="download-tiffs-btn"
                  color="inherit"
                  startIcon={<GetAppIcon />}
                  onClick={handleDownloadTiffs}
                >
                  {isWidthUp('sm', width)
                    ? t('Download')
                    : ''}
                </Button>
              )}
            {openEditParcel || downloadTiffs ? null : (
              <Button
                id="edit-parcels-info-btn"
                color="inherit"
                startIcon={<EditIcon />}
                onClick={handleEditName}
              >
                {isWidthUp('sm', width) ? t('edit-parcel') : ''}
              </Button>
            )}
          </Toolbar>
        </AppBar>
        {openEditParcel && (
          <EditParcel
            layers={layers}
            parcel={selectedParcel}
            setSelectedParcel={setSelectedParcel}
            dispatchFarms={dispatchFarms}
            dispatchAddedParcels={dispatchAddedParcels}
            handleClose={handleClose}
            farms={farms}
            editParcel={openEditParcel}
            selectedResolution={selectedResolution}
            farmsHectares={farmsHectares}
            selectedFarm={selectedFarm}
            setIsUpdating={setIsUpdating}
          />
        )}

        {downloadTiffs && loggedUser.is_superuser && (
          <DownloadTiffs
            layers={layers}
            parcel={selectedParcel}
            setSelectedParcel={setSelectedParcel}
            dispatchFarms={dispatchFarms}
            dispatchAddedParcels={dispatchAddedParcels}
            handleClose={handleClose}
            farms={farms}
            editParcel={openEditParcel}
            selectedResolution={selectedResolution}
            farmsHectares={farmsHectares}
          />
        )}

        {!openEditParcel && !downloadTiffs && (
          <>
            <EnabledTabs
              value={value}
              handleChange={handleChange}
              showBadge={showBadge}
              embeddedConf={loggedUser.profile?.embedded_conf}
              loggedUser={loggedUser}
            />

            {selectedParcel ? (
              <React.Fragment>
                <TabPanel
                  value={value}
                  index={0}
                  id="comparator-tab-panel"
                >
                  <Comparator
                    layers={layers}
                    parcel={selectedParcel}
                    baseImage={baseImage}
                    setBaseImage={setBaseImage}
                    comparedImage={comparedImage}
                    setComparedImage={setComparedImage}
                    selectedResolution={selectedResolution}
                    farms={farms}
                  />
                </TabPanel>
                <TabPanel
                  value={value}
                  index={1}
                  id="weather-forecast-tab-panel"
                >
                  <Forecast parcel={selectedParcel} />
                </TabPanel>
                <TabPanel
                  value={value}
                  index={2}
                  id="notes-tab"
                >
                  <NotesList
                    parcel={selectedParcel}
                    layers={layers}
                    selectedResolution={selectedResolution}
                    maxCC={maxCC}
                    setMaxCC={setMaxCC}
                    from_date={from_date}
                    toDate={toDate}
                    isLoadingParcelFISHistory={
                      isLoadingParcelFISHistory
                    }
                    dataParcelFISHistory={
                      dataParcelFISHistory
                    }
                    errorParcelFISHistory={
                      errorParcelFISHistory
                    }
                    refetchParcelFISHistory={
                      refetchParcelFISHistory
                    }
                    selectedLayer={selectedLayer}
                    handleSelectedLayer={
                      handleSelectedLayer
                    }
                  />
                </TabPanel>
                <TabPanel
                  value={value}
                  index={3}
                  id="statistics-tab-panel"
                >
                  <StatsGraph
                    layers={layers}
                    parcel={selectedParcel}
                    selectedResolution={selectedResolution}
                    maxCC={maxCC}
                    setMaxCC={setMaxCC}
                    isLoadingParcelFISHistory={
                      isLoadingParcelFISHistory
                    }
                    dataParcelFISHistory={
                      dataParcelFISHistory
                    }
                    errorParcelFISHistory={
                      errorParcelFISHistory
                    }
                    refetchParcelFISHistory={
                      refetchParcelFISHistory
                    }
                    selectedLayer={selectedLayer}
                    setSelectedLayer={setSelectedLayer}
                  ></StatsGraph>

                </TabPanel>
                <TabPanel
                  value={value}
                  index={4}
                  id="advanced-section-tab-panel"
                >
                  <AdvancedData
                    selectedResolution={selectedResolution}
                    parcel={selectedParcel}
                    layers={layers}
                    maxCC={maxCC}
                    setMaxCC={setMaxCC}
                    isLoadingParcelFISHistory={
                      isLoadingParcelFISHistory
                    }
                    dataParcelFISHistory={
                      dataParcelFISHistory
                    }
                    refetchParcelFISHistory={
                      refetchParcelFISHistory
                    }
                    selectedLayer={selectedLayer}
                    handleSelectedLayer={
                      handleSelectedLayer
                    }
                  ></AdvancedData>
                </TabPanel>
                <TabPanel
                  value={value}
                  index={5}
                  id="weather-forecast-tab-panel"
                >
                  <Crops parcel={selectedParcel}></Crops>
                </TabPanel>

                <TabPanel
                  value={value}
                  index={6}
                  id="prescription-maps-tab-panel"
                >
                  <Fertilizer
                    layers={layers}
                    parcel={selectedParcel}
                    selectedResolution={selectedResolution}
                  ></Fertilizer>
                </TabPanel>

                <TabPanel
                  value={value}
                  index={8}
                  id="plants-analysis"
                >
                  <TreeMap
                    key={selectedParcel.id}
                    farm={selectedFarm}
                    layers={layers}
                    parcel={selectedParcel}
                    selectedResolution={
                      selectedResolution
                    }
                  ></TreeMap>
                </TabPanel>

                {(loggedUser.is_superuser ||
                  loggedUser.username === 'widhoc' ||
                  loggedUser.username === 'greenfield' ||
                  loggedUser.username === 'cperez') && (
                    <TabPanel
                      value={value}
                      index={9}
                      id="vegetation-nutrition"
                    >
                      <VegetationNutrition></VegetationNutrition>
                    </TabPanel>
                  )}

                {(loggedUser.is_superuser ||
                  loggedUser?.profile.can_view_plagues ||
                  loggedUser?.profile
                    .can_view_and_add_plagues) && (
                    <TabPanel
                      value={value}
                      index={7}
                      id="plague-alert-tab-panel"
                    >
                      <PlaguesList
                        parcel={selectedParcel}
                      ></PlaguesList>
                    </TabPanel>
                  )}
              </React.Fragment>
            ) : null}
          </>
        )}
      </Dialog>
      {loggedUser.profile.show_intro && <DialogIntro />}
    </>
  )
}

export default withWidth()(FullScreenInfoDialog)
